<template>
  <div class="sign-address-form"
       v-loading="loading"
       element-loading-text="数据加载中，请稍等...">
    <template v-if="detailInfo && mapConfig.mapKey">
      <div class="main-cont">
        <el-form ref="addressForm"
                 :model="form"
                 :rules="rules"
                 size="mini"
                 label-width="100px"
                 label-suffix="：">
          <el-form-item label="所属教学点"
                        prop="siteId">
            <el-select v-if="detailInfo.siteSelectable"
                       v-model="form.siteId"
                       filterable
                       placeholder="请选择所属教学点">
              <el-option v-for="site in detailInfo.sites"
                         :key="site.id"
                         :label="site.name"
                         :value="site.id"></el-option>
            </el-select>
            <el-input v-else
                      :value="detailInfo.siteName"
                      disabled></el-input>
            <div class="field-tip">{{siteTip}}</div>
          </el-form-item>
          <el-form-item label="地址名称"
                        prop="name">
            <el-input v-model.trim="form.name"
                      maxlength="100"
                      show-word-limit
                      placeholder="请输入便于识别的地址名称"></el-input>
          </el-form-item>
          <el-form-item label="授课地点"
                        prop="address">
            <AddressSelector v-model="form.address"
                             :coordinates="form.tencentMapLocation"
                             :site-id="form.siteId"
                             :map-key="mapConfig.mapKey"
                             :circle-radius="mapRadius"
                             :library-enabled="false"
                             placeholder="请输入地址或打开地图选点"
                             @address-change="handleAddressChange">
            </AddressSelector>
          </el-form-item>
          <el-form-item label="地图坐标"
                        prop="tencentMapLocation">
            <el-input v-model="form.tencentMapLocation"
                      disabled
                      placeholder="请通过地图选择位置"></el-input>
          </el-form-item>
          <el-form-item label="备注"
                        prop="remark">
            <el-input v-model.trim="form.remark"
                      type="textarea"
                      :rows="3"
                      maxlength="500"
                      show-word-limit
                      placeholder="可填写楼栋、教室等补充信息"></el-input>
          </el-form-item>
        </el-form>
      </div>
      <div class="footer">
        <el-button size="mini"
                   type="primary"
                   :loading="committing"
                   @click="handleSubmit">保存</el-button>
        <el-button size="mini"
                   :disabled="committing"
                   @click="handleClose">取消</el-button>
      </div>
    </template>
    <Hint v-else-if="!loading"
          type="error">
      <div>地址表单加载失败</div>
      <el-button type="text"
                 @click="loadData">重新加载</el-button>
    </Hint>
  </div>
</template>

<script>
import AddressSelector from '../../../../../../../../widgets/tencent/AddressSelector'
import Hint from '../../../../../../../../elements/Hint'

export default {
  name: 'SignAddressForm',
  components: {
    AddressSelector,
    Hint
  },
  props: {
    rowData: {
      type: Object,
      default: () => ({})
    },
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      committing: false,
      detailInfo: null,
      mapConfig: {
        mapKey: '',
        maxSignDistance: 500
      },
      form: {
        siteId: '',
        name: '',
        address: '',
        tencentMapLocation: '',
        remark: ''
      },
      rules: {
        siteId: [
          { required: true, message: '请选择所属教学点', trigger: 'change' }
        ],
        name: [
          { required: true, message: '请输入地址名称', trigger: 'blur' },
          { max: 100, message: '地址名称不能超过100个字符', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '请输入或选择授课地点', trigger: 'change' },
          { max: 100, message: '授课地点不能超过100个字符', trigger: 'blur' }
        ],
        tencentMapLocation: [
          { required: true, message: '请通过地图选择位置', trigger: 'change' }
        ],
        remark: [
          { max: 500, message: '备注不能超过500个字符', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isUpdate () {
      return Boolean(this.rowData && this.rowData.id)
    },
    detailUrl () {
      return this.isUpdate
        ? '/workspace/manager/teaching/schedule/arrange/address/update/detailInfo'
        : '/workspace/manager/teaching/schedule/arrange/address/create/detailInfo'
    },
    submitUrl () {
      return this.isUpdate
        ? '/workspace/manager/teaching/schedule/arrange/address/update/submit'
        : '/workspace/manager/teaching/schedule/arrange/address/create/submit'
    },
    siteTip () {
      if (this.isUpdate) {
        return '所属教学点在修改时保持不变'
      }
      return this.detailInfo && this.detailInfo.siteSelectable
        ? '地址将保存到所选教学点'
        : '根据当前登录账号自动确定'
    },
    mapRadius () {
      return Number(this.mapConfig.maxSignDistance) || 500
    }
  },
  watch: {
    visible (value) {
      if (value) {
        this.loadData()
      }
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    resetForm () {
      this.detailInfo = null
      this.form = {
        siteId: '',
        name: '',
        address: '',
        tencentMapLocation: '',
        remark: ''
      }
      this.$nextTick(() => {
        if (this.$refs.addressForm) {
          this.$refs.addressForm.clearValidate()
        }
      })
    },
    loadData () {
      if (this.loading) {
        return
      }
      this.resetForm()
      this.loading = true
      Promise.all([
        this.loadDetailInfo(),
        this.loadMapConfig()
      ]).catch(error => {
        this.$message.warning(error.message || '地址表单加载失败')
      }).finally(() => {
        this.loading = false
      })
    },
    loadDetailInfo () {
      const params = this.isUpdate ? { id: this.rowData.id } : {}
      return this.$ajax({
        url: this.detailUrl,
        method: 'post',
        data: this.$qs.stringify(params)
      }).then(response => {
        if (!response.data || response.data.responseCode !== 'SUCCESS') {
          throw new Error(response.data && response.data.message
            ? response.data.message
            : '地址信息加载失败')
        }
        this.detailInfo = response.data
        this.form.siteId = response.data.siteId || ''
        this.form.name = response.data.name || ''
        this.form.address = response.data.address || ''
        this.form.tencentMapLocation = response.data.tencentMapLocation || ''
        this.form.remark = response.data.remark || ''
      }, () => {
        throw new Error('地址信息请求失败，请稍后重试')
      })
    },
    loadMapConfig () {
      return this.$ajax({
        url: '/support/thirdparty/tencent/map/queryTencentMapBaiscConfig',
        method: 'post'
      }).then(response => {
        if (!response.data || response.data.responseCode !== 'SUCCESS' || !response.data.mapKey) {
          throw new Error(response.data && response.data.message
            ? response.data.message
            : '地图配置加载失败')
        }
        this.mapConfig.mapKey = response.data.mapKey
        this.mapConfig.maxSignDistance = response.data.maxSignDistance || 500
      }, () => {
        throw new Error('地图配置请求失败，请稍后重试')
      })
    },
    handleAddressChange (address, coordinates) {
      this.form.address = address || ''
      this.form.tencentMapLocation = coordinates || ''
      this.$nextTick(() => {
        if (this.$refs.addressForm) {
          this.$refs.addressForm.validateField('address')
          this.$refs.addressForm.validateField('tencentMapLocation')
        }
      })
    },
    handleSubmit () {
      this.$refs.addressForm.validate(valid => {
        if (!valid) {
          return
        }
        this.committing = true
        const params = this.$extend({}, this.form)
        // 地址库的授课地点使用地址名称，地图坐标独立用于签到。
        params.address = params.name
        if (this.isUpdate) {
          params.id = this.rowData.id
        }
        this.$ajax({
          url: this.submitUrl,
          method: 'post',
          data: this.$qs.stringify(params)
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.$message.success(response.data.message)
            this.$emit('hidePopoverAndRefresh')
          } else if (response.data) {
            this.$message.warning(response.data.message)
          }
        }, () => {
          this.$message.error('保存失败，请稍后重试')
        }).finally(() => {
          this.committing = false
        })
      })
    },
    handleClose () {
      this.$emit('hidePopover')
    }
  }
}
</script>

<style lang="scss" scoped>
.sign-address-form {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > .main-cont {
    flex: auto;
    min-height: 0;
    overflow-y: auto;
    padding: 18px 24px 12px 14px;

    .field-tip {
      height: 20px;
      color: #909399;
      font-size: 12px;
      line-height: 20px;
    }
  }

  > .footer {
    flex: none;
    height: 52px;
    border-top: 1px solid rgba(0, 0, 0, .06);
    background: #fff;
    line-height: 50px;
    text-align: center;
  }

  .layout-hint {
    height: 100%;
  }
}
</style>
